/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import React, { useCallback, useRef, useState } from "react";
import EstimateCostDatabaseItems from "./EstimateCostDatabaseItemsGridNew";
import { itemListSources } from "@Actions/estimation";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import type { EstimateCurrency } from "@ServerTypes/estimates/estimates.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

type CurrencyData = EstimateCurrency;

/**
 * CostDatabase/estimate item rows shown in this picker. Originated from EstimateItems aggregation
 * (server `getEstimateItemsResponse.result` is typed as `array(record(string, unknown))`).
 * The frontend only reads/writes `level` here; other fields flow through unchanged.
 */
type CostDatabasePickerItem = {
  level?: number;
  __isDummy?: boolean;
  // EstimateItem-shaped rows expose `parentId` as `string | null | undefined`.
  parentId?: string | null;
  targetLevel?: number;
  [key: string]: unknown;
};

/**
 * The handle `EstimateCostDatabaseItems` writes into `initialTreeGridRef`, derived from that
 * component's own prop type so the two cannot drift — a hand-rolled copy of it could only be handed
 * to the prop through an assertion, and it hid the holes `getAllSelectedItems()` really returns.
 *
 * `slickGrid.getDataItem` is intersected in because the runtime object is SlickGrid1's
 * `SlickgridReactInstanceX` (its `slickGrid` IS the real SlickGrid), while that file's narrow
 * duck-type only lists the members it uses itself.
 */
type EstimateItemsTreeGridRef = NonNullable<
  NonNullable<React.ComponentProps<typeof EstimateCostDatabaseItems>["initialTreeGridRef"]>["current"]
>;

type TreeGridRef = EstimateItemsTreeGridRef & {
  slickGrid: {
    /** Nothing for a row index the view no longer holds — dropped in `handleOnSelect`. */
    getDataItem: (row: number) => CostDatabasePickerItem | undefined;
  };
};

type CostDatabasePopupProps = {
  estimateId: ObjectIdString;
  handleDiscardItemAdditionFromPopup: () => void;
  handleItemAdditionFromPopup: (items: CostDatabasePickerItem[]) => void;
  parentId: string;
  currencyData: CurrencyData;
  level: number;
  buttonLoadingState?: boolean;
};

function CostDatabasePopup(props: CostDatabasePopupProps) {
  const { t } = useTranslation();
  const {
    estimateId,
    handleDiscardItemAdditionFromPopup,
    handleItemAdditionFromPopup,
    parentId,
    currencyData,
    level,
    buttonLoadingState,
  } = props;

  const initialTreeGridRef = useRef<TreeGridRef | null>(null);

  const getValidItemsQuery = {
    estimateParentId: parentId,
    estimateId,
  };

  const [isItemSelected, setIsItemSelected] = useState(false);

  function handleOnSelect() {
    const grid = initialTreeGridRef.current?.slickGrid;
    const selectedItems = grid
      ? grid
          .getSelectedRows()
          .map((row) => grid.getDataItem(row))
          .filter((item): item is CostDatabasePickerItem => Boolean(item))
      : [];

    const selectedItemsUpdated = selectedItems.map((item) => ({
      ...item,
      parentId,
      targetLevel: level,
    }));
    selectedItemsUpdated.sort((a, b) => {
      const levelA = typeof a.level === "number" ? a.level : 0;
      const levelB = typeof b.level === "number" ? b.level : 0;
      return levelB - levelA;
    });

    handleItemAdditionFromPopup(selectedItemsUpdated);
  }

  function itemSelectionCallback() {
    const selectedItems =
      initialTreeGridRef.current?.dataView.getAllSelectedItems().filter((record) => record && !record.__isDummy) ?? [];
    setIsItemSelected(!!selectedItems.length);
  }

  const content = useCallback(
    () => (
      // `allowCheckboxSelection` used to be passed here too: EstimateCostDatabaseItems never reads
      // it (nor does anything it renders), it is a leftover of the legacy BoqHelper grid, and it was
      // the only reason this element went through a re-typed alias of the component.
      <EstimateCostDatabaseItems
        isStaticData={true}
        initialTreeGridRef={initialTreeGridRef}
        source={itemListSources.ADD_ITEM_POPUP}
        queryProp={getValidItemsQuery}
        itemSelectionCallback={itemSelectionCallback}
        currencyData={currencyData}
        enableSelectAll={false}
      />
    ),
    [],
  );

  function footer() {
    return (
      <div className="">
        <ButtonComponent
          disabled={!isItemSelected}
          onClick={handleOnSelect}
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          loading={buttonLoadingState}
          className="me-4"
        >
          {t("Select")}
        </ButtonComponent>
        <ButtonComponent
          onClick={handleDiscardItemAdditionFromPopup}
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
          disabled={buttonLoadingState}
        >
          {t("Cancel")}
        </ButtonComponent>
      </div>
    );
  }

  return (
    <ModalStateDialogComponent width="75rem" height="85%" modelContent={content()} footerContent={footer()} isVisible />
  );
}

export default CostDatabasePopup;
